import { pluginFactory } from '@atlaskit/editor-common/utils';
import type { EditorState, ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { DocumentChecker } from '../../utils/document-checker';
import type { RateLimiter } from '../../utils/rate-limiter';

import { aiProactivePluginKey } from './ai-proactive-plugin-key';
// import { DOC_CHANGE_COUNT_LIMIT } from './constants';
import { regenerateAllDecorations, regenerateSelectionDecorations } from './decorations';
import { reducer } from './reducer';
import type { AIProactivePluginState } from './states';
import {
	findProactiveAIBlockToUpdate,
	getSelectedRecommendations,
	initializeAllProactiveAIBlocks,
} from './utils';

const handleDocChanged = (
	tr: ReadonlyTransaction,
	pluginState: AIProactivePluginState,
	newEditorState: EditorState,
): AIProactivePluginState => {
	const { updatedProactiveAIBlock } = findProactiveAIBlockToUpdate(tr, pluginState, newEditorState);

	const { decorationSet } = pluginState;

	return {
		...pluginState,
		decorationSet: regenerateAllDecorations({
			decorationSet,
			tr,
			pluginState: {
				...pluginState,
				proactiveAIBlocks: updatedProactiveAIBlock,
			},
		}),
		proactiveAIBlocks: updatedProactiveAIBlock,
	};
};

const handleSelectionChanged = (
	tr: ReadonlyTransaction,
	pluginState: AIProactivePluginState,
): AIProactivePluginState => {
	const { decorationSet, selectedRecommendationId } = pluginState;

	const selectedRecommendations =
		tr.selection.from === tr.selection.to
			? getSelectedRecommendations(pluginState, tr.selection.from)
			: undefined;

	const hasSelectedId = !!selectedRecommendations?.find((r) => r.id === selectedRecommendationId)
		?.id;

	const nextState = {
		...pluginState,
		selectedRecommendationId: hasSelectedId ? selectedRecommendationId : undefined,
	};

	return {
		...nextState,
		decorationSet: regenerateSelectionDecorations({
			decorationSet,
			tr,
			pluginState: nextState,
		}),
	};
};

export const createInitialState =
	({
		proactiveAIApiUrl,
		documentChecker,
		rateLimiter,
		defaultToggledState,
		product,
	}: {
		proactiveAIApiUrl: string;
		documentChecker?: DocumentChecker;
		rateLimiter?: RateLimiter;
		defaultToggledState?: boolean;
		product: string;
	}) =>
	(state: EditorState) => {
		const { proactiveAIBlocks } = initializeAllProactiveAIBlocks(state);
		const initialToggleState = !!defaultToggledState;
		return {
			product,
			proactiveAIBlocks,
			initialToggleState,
			isProactiveEnabled: true,
			isProactiveContextPanelOpen: false, // was: initialToggleState
			canProactiveContextPanelOpen: false,
			alwaysDisplayProactiveInlineDecorations: false,
			alwaysOpenProactiveContextPanelOnInlineSelection: false,
			validateBlocksOnMissingRecommendations: true,
			allowInlineHoverHighlightWhileRecommendationSelected: true,
			decorationSet: DecorationSet.empty,
			proactiveAIApiUrl,
			// dismissedWords: new Set<string>(),
			availableRecommendationIds: new Set<string>(),
			toggleCount: initialToggleState ? 1 : 0,
			insertionCount: 0,
			dismissedCount: 0,
			documentChecker,
			rateLimiter,
			docChangeCountUntilLimit: 0,
			isFirstInitiatedEventSent: false,
			displayAllSuggestions: true,
		};
	};

export const { createPluginState, createCommand, getPluginState } = pluginFactory(
	aiProactivePluginKey,
	reducer,
	{
		onDocChanged: handleDocChanged,
		onSelectionChanged: handleSelectionChanged,
	},
);
